import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { page, wrap, card, h1, h2, muted, grid, label, input, backBtn, money, num, btn } from './ui';
import UnderConstructionBanner from '../../components/UnderConstructionBanner';

const pct = (cur, prior) => {
  const c = num(cur);
  const p = num(prior);
  if (!p) return '';
  const change = ((c - p) / p) * 100;
  return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
};

export default function MarketUpdate() {
  const navigate = useNavigate();
  const [v, setV] = useState({
    area: '', period: '', source: '', priceRange: '',
    medianNow: '', medianPriorYear: '', avgDom: '', activeListings: '', newListings: '',
    pending: '', closed: '', priceReductions: '',
    meaning: '', whatWeAreDoing: '',
  });
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });

  const medianChange = pct(v.medianNow, v.medianPriorYear);
  const stats = [
    { label: 'Median sold price', value: v.medianNow ? money(num(v.medianNow)) : '', note: medianChange ? `${medianChange} vs. same period last year` : '' },
    { label: 'Average days on market', value: v.avgDom ? `${num(v.avgDom)} days` : '' },
    { label: 'Active listings', value: v.activeListings },
    { label: 'New listings', value: v.newListings },
    { label: 'Pending sales', value: v.pending },
    { label: 'Closed sales', value: v.closed },
    { label: 'Price reductions', value: v.priceReductions },
  ].filter((s) => s.value);

  return (
    <div style={page} className="market-update-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, .market-update-page { background: #ffffff !important; padding: 0 !important; }
          .print-card { border: none !important; box-shadow: none !important; }
        }
      `}</style>
      <div style={wrap}>
        <button onClick={() => navigate('/tools')} style={backBtn} className="no-print">← Back to tools</button>
        <h1 style={h1} className="no-print">Seller Market Update</h1>
        <div className="no-print"><UnderConstructionBanner /></div>
        <p style={muted} className="no-print">Enter the numbers from your MLS or market report, then print or save the update as a PDF to send to a seller. Use the source and date so every number can be traced.</p>

        <div style={card} className="no-print">
          <h2 style={h2}>Market details</h2>
          <div style={grid}>
            <div><label style={label}>Area or community</label><input style={input} value={v.area} onChange={set('area')} placeholder="The Place at Corkscrew" /></div>
            <div><label style={label}>Period covered</label><input style={input} value={v.period} onChange={set('period')} placeholder="September 2026" /></div>
            <div><label style={label}>Price range</label><input style={input} value={v.priceRange} onChange={set('priceRange')} placeholder="$600K to $1.2M" /></div>
            <div><label style={label}>Data source</label><input style={input} value={v.source} onChange={set('source')} placeholder="NABOR MLS, run Oct 1, 2026" /></div>
          </div>

          <h2 style={{ ...h2, marginTop: '1.25rem' }}>Numbers</h2>
          <div style={grid}>
            <div><label style={label}>Median sold price, this period</label><input style={input} inputMode="numeric" value={v.medianNow} onChange={set('medianNow')} /></div>
            <div><label style={label}>Median sold price, same period last year</label><input style={input} inputMode="numeric" value={v.medianPriorYear} onChange={set('medianPriorYear')} /></div>
            <div><label style={label}>Average days on market</label><input style={input} inputMode="numeric" value={v.avgDom} onChange={set('avgDom')} /></div>
            <div><label style={label}>Active listings</label><input style={input} inputMode="numeric" value={v.activeListings} onChange={set('activeListings')} /></div>
            <div><label style={label}>New listings</label><input style={input} inputMode="numeric" value={v.newListings} onChange={set('newListings')} /></div>
            <div><label style={label}>Pending sales</label><input style={input} inputMode="numeric" value={v.pending} onChange={set('pending')} /></div>
            <div><label style={label}>Closed sales</label><input style={input} inputMode="numeric" value={v.closed} onChange={set('closed')} /></div>
            <div><label style={label}>Price reductions</label><input style={input} inputMode="numeric" value={v.priceReductions} onChange={set('priceReductions')} /></div>
          </div>

          <h2 style={{ ...h2, marginTop: '1.25rem' }}>Your commentary</h2>
          <div style={{ marginBottom: '0.9rem' }}>
            <label style={label}>What these numbers mean for sellers</label>
            <textarea style={{ ...input, minHeight: 80 }} value={v.meaning} onChange={set('meaning')} placeholder="Plain language. Note where a number is soft or where the trend is mixed." />
          </div>
          <div>
            <label style={label}>What we are doing for you</label>
            <textarea style={{ ...input, minHeight: 80 }} value={v.whatWeAreDoing} onChange={set('whatWeAreDoing')} placeholder="Marketing, showings, open houses, and any pricing updates." />
          </div>

          <div style={{ marginTop: '1.25rem' }}>
            <button onClick={() => window.print()} style={btn}>Print or save as PDF</button>
          </div>
        </div>

        <div style={{ ...card, background: '#ffffff' }} className="print-card">
          <img src="/residence-exp-logo.png" alt="RESIDENCE | eXp Realty" style={{ height: 56, width: 'auto', marginBottom: 16 }} />
          <div style={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8C6D1F', fontWeight: 700 }}>Seller Market Update</div>
          <h2 style={{ fontSize: 26, margin: '6px 0 4px', color: '#1a1a1a' }}>{v.area || 'Your Area'}</h2>
          <div style={{ color: '#7f7f7f', fontSize: 14, marginBottom: 18 }}>
            {[v.period, v.priceRange].filter(Boolean).join(' · ')}
          </div>

          {stats.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
              {stats.map((s) => (
                <div key={s.label} style={{ border: '1px solid #d8c9a8', borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ fontSize: 12, color: '#7f7f7f', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', marginTop: 4 }}>{s.value}</div>
                  {s.note && <div style={{ fontSize: 12, color: '#7f7f7f', marginTop: 2 }}>{s.note}</div>}
                </div>
              ))}
            </div>
          ) : (
            <p style={muted}>Enter numbers on the left to fill in this report.</p>
          )}

          {v.meaning && (
            <>
              <h3 style={{ fontSize: 16, color: '#1a1a1a', margin: '0 0 6px' }}>What this means for sellers</h3>
              <p style={{ fontSize: 15, lineHeight: 1.6, color: '#1a1a1a', whiteSpace: 'pre-wrap', marginTop: 0 }}>{v.meaning}</p>
            </>
          )}
          {v.whatWeAreDoing && (
            <>
              <h3 style={{ fontSize: 16, color: '#1a1a1a', margin: '18px 0 6px' }}>What we are doing for you</h3>
              <p style={{ fontSize: 15, lineHeight: 1.6, color: '#1a1a1a', whiteSpace: 'pre-wrap', marginTop: 0 }}>{v.whatWeAreDoing}</p>
            </>
          )}

          <div style={{ borderTop: '1px solid #d8c9a8', marginTop: 24, paddingTop: 16, fontSize: 14, color: '#1a1a1a', lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700 }}>Kelly Olin</div>
            <div>Co-Founder &amp; Team Leader | RESIDENCE | eXp Realty</div>
            <div>Call or text: 239-488-5900</div>
            <div>Kelly.Olin@eXpRealty.com</div>
          </div>
          {v.source && <div style={{ fontSize: 11, color: '#7f7f7f', marginTop: 14 }}>Source: {v.source}. Figures are as reported and may change as new data posts.</div>}
        </div>
      </div>
    </div>
  );
}
